import { Component, Input, ViewEncapsulation, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Observable } from 'rxjs';
import { ItemsDetailsService } from '../../services/items-details/items-details.service';
import { MovedInOutRow } from '../../models/moved-in-out-item';

type MovementTabId = 'loaded_from_other_routes' | 'loaded_to_other_routes';
type DialogColumn = { id: string; label: string };

@Component({
  selector: 'app-moved-in-out-dialog',
  templateUrl: './moved-in-out-dialog.component.html',
  styleUrls: ['./moved-in-out-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone:false
})
export class MovedInOutDialogComponent implements OnInit, OnChanges {
  @Input() routeDetails: any;

  movementTabs: MenuItem[] = [
    { id: 'loaded_from_other_routes', label: 'Loaded from other Routes' },
    { id: 'loaded_to_other_routes', label: 'Loaded to other Routes' }
  ];
  activeTab: MenuItem = this.movementTabs[0];

  movedInItems: MovedInOutRow[] = [];
  movedOutItems: MovedInOutRow[] = [];
  isLoading = false;
  wrapUnwrap = false;
  private movedInLoaded = false;
  private movedOutLoaded = false;

  readonly columnConfigurations: Record<MovementTabId, DialogColumn[]> = {
    loaded_from_other_routes: [
      { id: 'route_name', label: 'Planned Route' },
      { id: 'bl_name', label: 'BLOM Location' },
      { id: 'receiver_name', label: 'Receiver Name' },
      { id: 'address', label: 'Address' },
      { id: 'barcode', label: 'Barcode' },
      { id: 'time_window', label: 'Time Window' },
      { id: 'item_state', label: 'Item State' },
      { id: 'product_code', label: 'Product Code' },
      { id: 'asc', label: 'ASC' }
    ],
    loaded_to_other_routes: [
      { id: 'route_name', label: 'Current Route' },
      { id: 'bl_name', label: 'BLOM Location' },
      { id: 'receiver_name', label: 'Receiver Name' },
      { id: 'address', label: 'Address' },
      { id: 'barcode', label: 'Barcode' },
      { id: 'time_window', label: 'Time Window' },
      { id: 'item_state', label: 'Item State' },
      { id: 'product_code', label: 'Product Code' },
      { id: 'asc', label: 'ASC' }
    ]
  };

  constructor(
    private readonly itemsDetailsService: ItemsDetailsService
  ) {}

  // Initializes the dialog data when the component is created.
  ngOnInit(): void {
    this.loadItemsData();
  }

  // Reloads data when route details input changes.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['routeDetails'] && !changes['routeDetails'].firstChange) {
      this.movedInLoaded = false;
      this.movedOutLoaded = false;
      this.loadItemsData();
    }
  }

  // Fetches moved-in and moved-out items for the selected route.
  private loadItemsData(): void {
    if (!this.routeDetails?.route_id) {
      return;
    }

    this.loadItemsForTab(this.currentTabId);
  }

  // Returns rows for the currently selected movement tab.
  get currentRows(): MovedInOutRow[] {
    return this.currentTabId === 'loaded_to_other_routes' ? this.movedOutItems : this.movedInItems;
  }

  // Returns the visible column configuration for the active tab.
  get currentColumns(): DialogColumn[] {
    return this.columnConfigurations[this.currentTabId];
  }

  // Resolves the internal tab id from the active tab menu item.
  private get currentTabId(): MovementTabId {
    return this.activeTab?.id === 'loaded_to_other_routes'
      ? 'loaded_to_other_routes'
      : 'loaded_from_other_routes';
  }

  // Updates the active tab when user switches between movement tabs.
  onTabMenuChange(tabId: string | number | undefined): void {
    const tab = this.movementTabs.find(t => t.id === tabId);
    if (!tab || tab.id === this.activeTab?.id) {
      return;
    }
    this.activeTab = tab;
    this.loadItemsData();
  }
  
  /**
   * Loads items for the specified tab. 
   * It checks if the data is already loaded to avoid unnecessary API calls.
   */
  private loadItemsForTab(tabId: MovementTabId): void {
    const routeId = this.routeDetails?.route_id;
    if (!routeId) {
      return;
    }

    const shouldLoad = this.getTabCount(tabId) > 0;
    if (!shouldLoad) {
      this.setTabItems(tabId, []);
      this.setTabLoaded(tabId, true);
      this.isLoading = false;
      return;
    }

    if (this.isTabLoaded(tabId)) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.getTabRequest(tabId, routeId).subscribe({
      next: (rows) => {
        this.setTabItems(tabId, rows);
        this.setTabLoaded(tabId, true);
        this.isLoading = false;
      },
      error: () => {
        this.setTabItems(tabId, []);
        this.setTabLoaded(tabId, true);
        this.isLoading = false;
      }
    });
  }
  
  /**
   * Returns the count of items for the specified tab.
   * @param tabId The ID of the tab.
   * @returns The number of items for the tab.
   */
  private getTabCount(tabId: MovementTabId): number {
    return tabId === 'loaded_from_other_routes'
      ? (this.routeDetails?.kpi?.items?.items_moved_in ?? 0)
      : (this.routeDetails?.kpi?.items?.items_moved_out ?? 0);
  }
  
  /**
   * Checks if the data for the specified tab has been loaded.
   * @param tabId The ID of the tab.
   * @returns True if the tab data is loaded, false otherwise.
   */
  private isTabLoaded(tabId: MovementTabId): boolean {
    return tabId === 'loaded_from_other_routes' ? this.movedInLoaded : this.movedOutLoaded;
  }

  /**
   * Sets the loaded state for the specified tab.
   * @param tabId The ID of the tab.
   * @param loaded The loaded state to set.
   */
  private setTabLoaded(tabId: MovementTabId, loaded: boolean): void {
    if (tabId === 'loaded_from_other_routes') {
      this.movedInLoaded = loaded;
      return;
    }
    this.movedOutLoaded = loaded;
  }
  /**
   * Sets the items for the specified tab.
   * @param tabId The ID of the tab.
   * @param items The items to set for the tab.
   */
  private setTabItems(tabId: MovementTabId, items: MovedInOutRow[]): void {
    if (tabId === 'loaded_from_other_routes') {
      this.movedInItems = items;
      return;
    }
    this.movedOutItems = items;
  }
  
  /**
   * Returns the API request observable for the specified tab and route.
   * @param tabId The ID of the tab.
   * @param routeId The ID of the route.
   * @returns An observable for the API request.
   */
  private getTabRequest(tabId: MovementTabId, routeId: string): Observable<MovedInOutRow[]> {
    return tabId === 'loaded_from_other_routes'
      ? this.itemsDetailsService.getMovedInRows(routeId)
      : this.itemsDetailsService.getMovedOutRows(routeId);
  }

  // Opens route details in a new tab for the selected row.
  openRouteInfo(row: MovedInOutRow): void {
    const routeId = row?.['route_id'];
    if (!routeId) {
      return;
    }

    sessionStorage.setItem('route_Id', routeId);
    window.open('#/routeinfo', '_blank');
  }

  // Opens barcode lookup in a new tab for the selected row.
  barcodeClicked(row: MovedInOutRow): void {
    const barcode = row?.barcode;
    if (barcode) {
      window.open('#/lookup?barcode=' + encodeURIComponent(barcode), '_blank');
    }
  }

}